import React, { useEffect, useState } from "react";

import { Form, Modal } from "react-bootstrap";
import { CalendarIcon, FilterIcon, UserIcon } from "../Icons";
import { FaDollarSign, FaPlus } from "react-icons/fa";
import "../../assets/scss/create-target.scss";
import NumberCustomInput from "../utils/NumberCustomInput";
import Select from "react-select";
import Datetime from "react-datetime";
import { debounce, isEmpty, uniqBy } from "lodash";
import AsyncSelect from "react-select/async";
import { useAuth } from "../../hooks/useAuth";
import { useMutation, useQuery } from "react-query";
import { customerFullName, customerTypeOptions } from "../../utils/helpers";

import { toast } from "react-toastify";

import { isValid, addMonths, addYears } from "date-fns";

function getDateRange(period, referenceDate = new Date()) {
  // Validate reference date
  if (!isValid(referenceDate)) {
    throw new Error("Invalid reference date provided");
  }

  const now = new Date(referenceDate);
  let startDate = now;
  let endDate = now;

  // Normalize period input (remove extra spaces, convert to lowercase)
  const normalizedPeriod = period.trim().toLowerCase();

  switch (normalizedPeriod) {
    case "monthly":
      // Last 1 month from current date
      endDate = addMonths(now, 1);
      break;

    case "quarterly":
      // Last 3 months from current date
      endDate = addMonths(now, 3);
      break;

    case "bi-annual":
    case "biannual":
    case "semi-annual":
    case "semiannual":
      // Last 6 months from current date
      endDate = addMonths(now, 6);
      break;

    case "yearly":
    case "annual":
      // Last 12 months from current date
      endDate = addYears(now, 1);
      break;

    default:
      throw new Error(
        `Invalid period: ${period}. Supported periods: daily, weekly, monthly, quarterly, bi-annual, yearly`
      );
  }

  return {
    startDate,
    endDate,
    period: normalizedPeriod,
  };
}

const TargetCreationModal = ({
  show,
  setShow,
  refetch,
  isUpdate = false,
  target,
}) => {
  const [activeTab, setActiveTab] = useState("customer");
  const [customerTarget, setCustomerTarget] = useState({
    targetValue: 1,
    period: "monthly",
    startDate: getDateRange("monthly").startDate,
    endDate: getDateRange("monthly").endDate,
  });
  const { backendUrl, token } = useAuth();
  const [revenueTarget, setRevenueTarget] = useState({
    targetValue: 1,
    period: "monthly",
    targetType: "general",
    customerType: "",
    customerGroup: "",
    specificCustomer: "",

    startDate: getDateRange("monthly").startDate,
    endDate: getDateRange("monthly").endDate,
  });

  useEffect(() => {
    if (isUpdate && !isEmpty(target)) {
      if (target.companyTarget === "customer") {
        setCustomerTarget({
          ...customerTarget,
          period: target.period,
          targetValue: Number(target.targetAmount),
          endDate: new Date(target.endDate),
          startDate: new Date(target.startDate),
        });
      } else {
        setRevenueTarget({
          ...revenueTarget,
          period: target.period,
          targetValue: Number(target.targetAmount),
          endDate: new Date(target.endDate),
          startDate: new Date(target.startDate),
          customerGroup: target.targetGroup,
          customerType: target.targetGroup,
          specificCustomer:
            target.targetType === "specific-customer" ? target.targetGroup : "",
          targetType: target.targetType,
        });
      }
      setActiveTab(target.companyTarget);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createTarget = async (payload) => {
    let endpoint = isUpdate
      ? `${backendUrl}/api/company/update-target/${target.id}`
      : `${backendUrl}/api/company/create-target`;
    let response = await fetch(endpoint, {
      method: isUpdate ? "PATCH" : "POST",
      credentials: "include",
      body: JSON.stringify(payload),
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const res = await response.json();
    return res;
  };

  const createTargetMutation = useMutation((payload) => createTarget(payload), {
    onSuccess: ({ message }) => {
      refetch();
      toast.success(message);
      setShow(false);
    },
    onError: ({ message = "" }) => {
      toast.error(`Unable to perform action: ${message}`);
    },
  });

  const periods = [
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "bi-annual", label: "Bi-Annual" },
    { value: "yearly", label: "Yearly" },
  ];

  const revenueTargetTypes = [
    { value: "general", label: "General" },
    { value: "customer-type", label: "By Customer Type" },
    { value: "customer-group", label: "By Customer Group" },
    { value: "specific-customer", label: "Specific Customer" },
  ];

  const handleCustomerTargetSubmit = () => {
    const payload = {
      companyTarget: activeTab,
      startDate: customerTarget.startDate,
      endDate: customerTarget.endDate,
      targetAmount: customerTarget.targetValue,
      period: customerTarget.period,
      targetType: "in-company",
      targetGroup: "in-company",
    };
    createTargetMutation.mutate(payload);
  };

  const handleRevenueTargetSubmit = async () => {
    const payload = {
      companyTarget: activeTab,
      startDate: revenueTarget.startDate,
      endDate: revenueTarget.endDate,
      targetAmount: revenueTarget.targetValue,
      period: revenueTarget.period,
      targetType: revenueTarget.targetType,
      targetGroup:
        revenueTarget.targetType === "general"
          ? "All customer"
          : revenueTarget.targetType === "customer-type"
          ? revenueTarget.customerType
          : revenueTarget.targetType === "customer-group"
          ? revenueTarget.customerGroup
          : revenueTarget.specificCustomer,
    };

    createTargetMutation.mutate(payload);
  };
  const fetchCustomers = async (inputValue = "", callback) => {
    try {
      let response = await fetch(
        `${backendUrl}/api/customers?page=1&Limit=50&LastName=${inputValue}`,
        {
          method: "GET",
          headers: {
            Accept: "Application/json",
            "Content-Type": "Application/json",
          },
          credentials: "include",
        }
      );
      if (!response.ok) {
        response = await response.json();
        throw new Error(response.message);
      }
      const { data } = await response.json();
      if (data.customers) {
        data.customers = data.customers.map((el) => ({
          ...el,
          label: customerFullName(el),
          value: el.Cust_ID,
        }));

        if (callback) {
          callback(data?.customers || []);
        }
      }

      data.customerTypes = uniqBy(
        [
          ...customerTypeOptions,
          ...data.customerTypes
            .filter((el) => el.TransType)
            .map((el) => ({
              value: el.TransType,
              label: el.TransType,
            })),
        ],
        "value"
      );

      data.groups = uniqBy(
        [
          ...data.groups
            .filter((el) => el.Group)
            .map((el) => ({
              value: el.Group,
              label: el.Group,
            })),
        ],
        "value"
      );

      return data;
    } catch (err) {
      console.log(err);
      callback([]);
    }
  };

  const { data } = useQuery(
    ["CUSTOMERS_LIST"],
    () => fetchCustomers(),
    { onSuccess: (data) => {} },
    {
      enabled: true,
      keepPreviousData: true,
    }
  );

  const debouncedFetchUsers = debounce(fetchCustomers, 500);

  const loadOptions = (inputValue, callback) => {
    try {
      if (isEmpty(inputValue)) {
        return callback(null, { options: [] });
      }

      debouncedFetchUsers(inputValue, callback);
    } catch (err) {
      console.log(err);
      return callback(null, { options: [] });
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => setShow(false)}
      animation={false}
      enforceFocus={false}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title as={"div"}>
          <h6>
            {isUpdate ? "Edit" : "Create"}
            Target
          </h6>
          <p>
            {isUpdate ? "Edit" : "Create"} and manage your customer acquisition
            and revenue targets
          </p>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={"container"}>
          <div className={"tab-container"}>
            <button
              className={`tab ${
                activeTab === "customer" ? "active-nav" : "inactive-nav"
              }`}
              onClick={() => setActiveTab("customer")}
            >
              <UserIcon size={20} />
              Customer Acquisition
            </button>
            <button
              className={`tab ${
                activeTab === "revenue" ? "active-nav" : "inactive-nav"
              }`}
              onClick={() => setActiveTab("revenue")}
            >
              <FaDollarSign size={20} />
              Revenue Target
            </button>
          </div>

          {activeTab === "customer" && (
            <div className={"form-container"}>
              <h6 className={"form-title"}>
                <UserIcon size={24} />
                Customer Acquisition Target
              </h6>
              <div>
                <div className={"form-grid"}>
                  <Form.Group className={"form-group"}>
                    <Form.Label>Target Number of Customers</Form.Label>

                    <NumberCustomInput
                      placeholder="0"
                      name="targetValue"
                      value={customerTarget.targetValue}
                      onValueChange={(value, name) => {
                        setCustomerTarget({
                          ...customerTarget,
                          targetValue: value,
                        });
                      }}

                      // disabled={true}
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>
                      <CalendarIcon
                        size={16}
                        style={{ display: "inline", marginRight: "5px" }}
                      />
                      Target Period
                    </Form.Label>

                    <Select
                      classNamePrefix={`form-select`}
                      options={periods}
                      value={periods.find(
                        (el) => el.value === customerTarget.period
                      )}
                      onChange={({ value }) => {
                        const date = getDateRange(value);

                        setCustomerTarget({
                          ...customerTarget,
                          period: value,
                          startDate: date.startDate,
                          endDate: date.endDate,
                        });
                      }}
                    />
                  </Form.Group>

                  <div className="row">
                    <Form.Group className=" col-md-6">
                      <Form.Label>Start Date</Form.Label>
                      <Datetime
                        dateFormat="MMM DD, YYYY"
                        timeFormat={false}
                        closeOnSelect={true}
                        closeOnClickOutside={true}
                        name="startDate"
                        inputProps={{
                          className: `date-input form-control  `,
                          placeholder: "Select date",
                          readOnly: true,
                        }}
                        value={customerTarget.startDate}
                        onChange={(date) => {
                          setCustomerTarget({
                            ...customerTarget,
                            startDate: new Date(date).toISOString(),
                          });
                        }}
                        // onBlur={() => formik.setFieldTouched("CompanyDateCreated", true)}
                      />
                    </Form.Group>
                    <Form.Group className=" col-md-6">
                      <Form.Label>End Date</Form.Label>

                      <Datetime
                        dateFormat="MMM DD, YYYY"
                        timeFormat={false}
                        closeOnSelect={true}
                        closeOnClickOutside={true}
                        name="endDate"
                        inputProps={{
                          className: `date-input form-control  `,
                          placeholder: "Select date",
                          readOnly: true,
                        }}
                        value={customerTarget.endDate}
                        onChange={(date) => {
                          setCustomerTarget({
                            ...customerTarget,
                            endDate: new Date(date).toISOString(),
                          });
                        }}
                        // onBlur={() => formik.setFieldTouched("CompanyDateCreated", true)}
                      />
                    </Form.Group>
                  </div>
                </div>

                <button
                  onClick={handleCustomerTargetSubmit}
                  className={"submit-button"}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#083d7a";
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow =
                      "0 4px 12px rgba(10, 74, 149, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#0a4a95";
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  <FaPlus size={20} />
                  {createTargetMutation.isLoading
                    ? " Creating"
                    : " Create Customer Target"}
                </button>
              </div>
            </div>
          )}

          {activeTab === "revenue" && (
            <div className={"form-container"}>
              <h6 className={"form-title"}>
                <FaDollarSign size={24} />
                Revenue Target
              </h6>
              <div>
                <div className={"form-grid"}>
                  <Form.Group>
                    <Form.Label> Target Revenue Amount </Form.Label>

                    <NumberCustomInput
                      placeholder="0"
                      name="targetValue"
                      value={revenueTarget.targetValue}
                      onValueChange={(value, name) => {
                        setRevenueTarget({
                          ...revenueTarget,
                          targetValue: value,
                        });
                      }}

                      // disabled={true}
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>
                      <CalendarIcon
                        size={16}
                        style={{ display: "inline", marginRight: "5px" }}
                      />
                      Target Period
                    </Form.Label>
                    <Select
                      classNamePrefix={`form-select`}
                      options={periods}
                      value={periods.find(
                        (el) => el.value === revenueTarget.period
                      )}
                      onChange={({ value }) => {
                        const date = getDateRange(value);

                        setRevenueTarget({
                          ...revenueTarget,
                          period: value,
                          startDate: date.startDate,
                          endDate: date.endDate,
                        });
                      }}
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>
                      <FilterIcon
                        size={16}
                        style={{ display: "inline", marginRight: "5px" }}
                      />
                      Target Type
                    </Form.Label>
                    <Select
                      classNamePrefix={`form-select`}
                      options={revenueTargetTypes}
                      isSearchable
                      value={revenueTargetTypes.find(
                        (el) => el.value === revenueTarget.targetType
                      )}
                      onChange={({ value }) => {
                        setRevenueTarget({
                          ...revenueTarget,
                          targetType: value,
                        });
                      }}
                    />
                  </Form.Group>

                  {revenueTarget.targetType === "customer-type" && (
                    <div className={"conditional-field"}>
                      <Form.Group>
                        <Form.Label>Customer Type</Form.Label>
                        <Select
                          classNamePrefix={`form-select`}
                          options={data?.customerTypes || []}
                          isSearchable
                          value={data?.customerTypes.find(
                            (el) => el.value === revenueTarget.customerType
                          )}
                          onChange={({ value }) => {
                            setRevenueTarget({
                              ...revenueTarget,
                              customerType: value,
                            });
                          }}
                        />
                      </Form.Group>
                    </div>
                  )}

                  {revenueTarget.targetType === "customer-group" && (
                    <div className={"conditional-field"}>
                      <Form.Group>
                        <Form.Label>Customer Group</Form.Label>

                        <Select
                          classNamePrefix={`form-select`}
                          isSearchable
                          options={data?.groups || []}
                          value={data?.groups.find(
                            (el) => el.value === revenueTarget.customerGroup
                          )}
                          onChange={({ value }) => {
                            setRevenueTarget({
                              ...revenueTarget,
                              customerGroup: value,
                            });
                          }}
                        />

                        {/* <select
                          className={"select"}
                          value={revenueTarget.customerGroup}
                          onChange={(e) =>
                            setRevenueTarget({
                              ...revenueTarget,
                              customerGroup: e.target.value,
                            })
                          }
                        >
                          <option value="">Select Customer Group</option>
                          {customerGroups.map((group) => (
                            <option key={group.value} value={group.value}>
                              {group.label}
                            </option>
                          ))}
                        </select> */}
                      </Form.Group>
                    </div>
                  )}

                  {revenueTarget.targetType === "specific-customer" && (
                    <div className={"conditional-field"}>
                      <Form.Group>
                        <Form.Label>Specific Customer</Form.Label>

                        <AsyncSelect
                          classNamePrefix={"form-select"}
                          className="customer-select-dropdown w-100"
                          data={data?.customers || []}
                          menuPlacement="bottom"
                          menuPosition="fixed"
                          isSearchable={true}
                          value={data?.customers?.find(
                            (el) => el?.value === revenueTarget.specificCustomer
                          )}
                          defaultOptions={data?.customers || []}
                          onChange={(selection) => {
                            setRevenueTarget({
                              ...revenueTarget,
                              specificCustomer: selection.value,
                            });
                          }}
                          id={`newInstallment-customer`}
                          loadOptions={loadOptions}
                        />
                      </Form.Group>
                    </div>
                  )}

                  <div className={"row"}>
                    <Form.Group className=" col-md-6">
                      <Form.Label>Start Date</Form.Label>
                      <Datetime
                        dateFormat="MMM DD, YYYY"
                        timeFormat={false}
                        closeOnSelect={true}
                        closeOnClickOutside={true}
                        name="startDate"
                        inputProps={{
                          className: `date-input form-control  `,
                          placeholder: "Select date",
                          readOnly: true,
                        }}
                        value={revenueTarget.startDate}
                        onChange={(date) => {
                          setRevenueTarget({
                            ...revenueTarget,
                            startDate: new Date(date).toISOString(),
                          });
                        }}
                        // onBlur={() => formik.setFieldTouched("CompanyDateCreated", true)}
                      />

                      {/* <DatePickerCustomInput/> */}
                    </Form.Group>
                    <Form.Group className="col-md-6">
                      <Form.Label>End Date</Form.Label>

                      <Datetime
                        dateFormat="MMM DD, YYYY"
                        timeFormat={false}
                        closeOnSelect={true}
                        closeOnClickOutside={true}
                        name="endDate"
                        inputProps={{
                          className: `date-input form-control  `,
                          placeholder: "Select date",
                          readOnly: true,
                        }}
                        value={revenueTarget.endDate}
                        onChange={(date) => {
                          setRevenueTarget({
                            ...revenueTarget,
                            endDate: new Date(date).toISOString(),
                          });
                        }}
                        // onBlur={() => formik.setFieldTouched("CompanyDateCreated", true)}
                      />
                    </Form.Group>
                  </div>
                </div>

                <button
                  onClick={handleRevenueTargetSubmit}
                  className={"submit-button"}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#083d7a";
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow =
                      "0 4px 12px rgba(10, 74, 149, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#0a4a95";
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  <FaPlus size={20} />
                  {createTargetMutation.isLoading
                    ? " Creating"
                    : " Create Revenue Target"}
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default TargetCreationModal;
